import { ApolloClient } from "@apollo/client";
import {
  BaseDataProvider,
  DataProviderParams,
  DataProviderResult,
} from "../base/BaseDataProvider";
import {
  GET_SUBSCRIPTION_PLANS,
  GET_SUBSCRIPTION_LIMITERS,
  GET_USER_SUBSCRIPTIONS,
  GET_SUBSCRIPTION_STATS,
  GET_LIMITER_STATS,
  CREATE_SUBSCRIPTION_PLAN,
  UPDATE_SUBSCRIPTION_PLAN,
  DELETE_SUBSCRIPTION_PLAN,
  CREATE_SUBSCRIPTION_LIMITER,
  UPDATE_SUBSCRIPTION_LIMITER,
  DELETE_SUBSCRIPTION_LIMITER,
  CREATE_USER_SUBSCRIPTION,
  UPDATE_USER_SUBSCRIPTION,
  DELETE_USER_SUBSCRIPTION,
} from "../queries/subscriptions.queries";

export class SubscriptionPlansDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_SUBSCRIPTION_PLANS,
        fetchPolicy: "network-only",
      });

      const data = result.data.subscriptionPlans || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    // For subscription plans, we'll get from the list
    const listResult = await this.getList(resource, params);
    const plan = listResult.data.find((p: any) => p.id === params.id);

    if (!plan) {
      throw new Error(`Subscription plan with id ${params.id} not found`);
    }

    return { data: plan };
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "create", params);

    try {
      const result = await this.client.mutate({
        mutation: CREATE_SUBSCRIPTION_PLAN,
        variables: { input: params.data },
      });

      const data = result.data.createSubscriptionPlan;
      this.logSuccess(resource, "create", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "create", error, params);
      throw error;
    }
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "update", params);

    try {
      const result = await this.client.mutate({
        mutation: UPDATE_SUBSCRIPTION_PLAN,
        variables: { input: { id: params.id, ...params.data } },
      });

      const data = result.data.updateSubscriptionPlan;
      this.logSuccess(resource, "update", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "update", error, params);
      throw error;
    }
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "delete", params);

    try {
      const result = await this.client.mutate({
        mutation: DELETE_SUBSCRIPTION_PLAN,
        variables: { id: params.id },
      });

      this.logSuccess(resource, "delete", result.data.deleteSubscriptionPlan);

      return { data: { id: params.id } };
    } catch (error: any) {
      this.logError(resource, "delete", error, params);
      throw error;
    }
  }
}

export class SubscriptionLimitersDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_SUBSCRIPTION_LIMITERS,
        fetchPolicy: "network-only",
      });

      const data = result.data.subscriptionLimiters || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    const limiter = listResult.data.find((l: any) => l.id === params.id);

    if (!limiter) {
      throw new Error(`Subscription limiter with id ${params.id} not found`);
    }

    return { data: limiter };
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "create", params);

    try {
      const result = await this.client.mutate({
        mutation: CREATE_SUBSCRIPTION_LIMITER,
        variables: { input: params.data },
      });

      const data = result.data.createSubscriptionLimiter;
      this.logSuccess(resource, "create", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "create", error, params);
      throw error;
    }
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "update", params);

    try {
      const result = await this.client.mutate({
        mutation: UPDATE_SUBSCRIPTION_LIMITER,
        variables: { input: { id: params.id, ...params.data } },
      });

      const data = result.data.updateSubscriptionLimiter;
      this.logSuccess(resource, "update", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "update", error, params);
      throw error;
    }
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "delete", params);

    try {
      const result = await this.client.mutate({
        mutation: DELETE_SUBSCRIPTION_LIMITER,
        variables: { id: params.id },
      });

      this.logSuccess(
        resource,
        "delete",
        result.data.deleteSubscriptionLimiter,
      );

      return { data: { id: params.id } };
    } catch (error: any) {
      this.logError(resource, "delete", error, params);
      throw error;
    }
  }
}

export class UserSubscriptionsDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_USER_SUBSCRIPTIONS,
        fetchPolicy: "network-only",
      });

      const data = result.data.adminGetUserSubscriptions || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    const subscription = listResult.data.find((s: any) => s.id === params.id);

    if (!subscription) {
      throw new Error(`User subscription with id ${params.id} not found`);
    }

    return { data: subscription };
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "create", params);

    try {
      const result = await this.client.mutate({
        mutation: CREATE_USER_SUBSCRIPTION,
        variables: { input: params.data },
      });

      const data = result.data.createUserSubscription;
      this.logSuccess(resource, "create", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "create", error, params);
      throw error;
    }
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "update", params);

    try {
      const result = await this.client.mutate({
        mutation: UPDATE_USER_SUBSCRIPTION,
        variables: { input: { id: params.id, ...params.data } },
      });

      const data = result.data.updateUserSubscription;
      this.logSuccess(resource, "update", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "update", error, params);
      throw error;
    }
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "delete", params);

    try {
      const result = await this.client.mutate({
        mutation: DELETE_USER_SUBSCRIPTION,
        variables: { id: params.id },
      });

      this.logSuccess(resource, "delete", result.data.deleteUserSubscription);

      return { data: { id: params.id } };
    } catch (error: any) {
      this.logError(resource, "delete", error, params);
      throw error;
    }
  }
}

export class SubscriptionStatsDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_SUBSCRIPTION_STATS,
        fetchPolicy: "network-only",
      });

      const data = result.data.adminGetSubscriptionStats;
      this.logSuccess(resource, "getList", data);

      return {
        data: [data], // Wrap in array for consistency
        total: 1,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    return { data: listResult.data[0] };
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Create not implemented for subscription stats:", params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Update not implemented for subscription stats:", params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Delete not implemented for subscription stats:", params);
    return { data: { id: params.id } };
  }
}

export class LimiterStatsDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_LIMITER_STATS,
        fetchPolicy: "network-only",
      });

      const data = result.data.adminGetLimiterStats || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    const stat = listResult.data.find((s: any) => s.limiterType === params.id);

    if (!stat) {
      throw new Error(`Limiter stat with type ${params.id} not found`);
    }

    return { data: stat };
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Create not implemented for limiter stats:", params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Update not implemented for limiter stats:", params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Delete not implemented for limiter stats:", params);
    return { data: { id: params.id } };
  }
}
